<template>
  <main v-style:max-w="{ lg: brandStyles?.main?.maxWidth }" :class="brandClasses?.main">
    <h1 v-if="!titleBelowBreadcrumb" class="mb-6 subtitle-1">
      {{ title }}
    </h1>
    <base-interpolator v-if="pageDescription" :content="pageDescription">
      <template #link="{ args: [message, to] }">
        <vf-link :to>
          {{ message }}
        </vf-link>
      </template>
    </base-interpolator>
    <section v-if="chartData" :class="brandClasses?.container">
      <vf-tabs
        v-style:max-w="{ lg: brandStyles?.tabs?.maxWidth }"
        :active="activeTab"
        class="mt-4 subtitle-5"
        :tabs="tabs.map(({ title }) => title)"
        variant="left"
        @change="onTabChange"
      >
        <template v-for="({ pageData }, i) in tabs" :key="i" #[`content-${i}`]>
          <div v-if="staticPageTabs?.[i]?.headline" class="b-b b-grey-70">
            <p class="my-8">
              {{ staticPageTabs[i].headline }}
            </p>
          </div>
          <vf-accordion
            v-for="([categoryTitle, items], j) in Object.entries(pageData ?? {})"
            :key="categoryTitle"
            v-style="{
              'm': { sm: !i && brandStyles?.accordion?.margin },
              'max-w': { lg: brandStyles?.accordion?.maxWidth },
            }"
            class="py-1 md:pl-0"
            :class="{ 'b-0': !i, 'b-t-0': !j }"
            class-title="body-3 fw-normal"
            :open="activeAccordion"
            size="sm"
            :title="categoryTitle"
            :value="`${j}`"
            variant="aligned"
            @click="updateQuery(`${i}`, categoryTitle)"
          >
            <div
              v-for="({
                list,
                order,
                title: tableTitle,
                measurements: chartMeasurements,
                fits: sizeChartFits,
                measurementsIndex }, k)
                in normalizeMeasurementsData(items)"
              :key="k"
            >
              <div class="flex items-center between">
                <div class="justify-start pl-1">
                  <vf-switch
                    v-if="list[0]?.toggle"
                    class="my-2"
                    :label-off="list[0].toggle"
                    :label-on="list[1].toggle"
                    :model-value="toggle[`${categoryTitle}${i}${k}`] === list[1].toggle"
                    @update:model-value="(value) =>
                      toggle[`${categoryTitle}${i}${k}`] = (value ? list[1].toggle : list[0].toggle)"
                  />
                </div>
                <div v-if="chartMeasurements" class="px-3 text-right">
                  <vf-link
                    v-if="howToMeasureLink"
                    @click="scrollToElement(`howToMeasure-${categoryTitle}-${i}-${measurementsIndex}`)"
                  >
                    {{ howToMeasureLink }}
                  </vf-link>
                </div>
              </div>
              <h2 :id="getHeadingId(tabs[i].title, tableTitle)" class="my-4 subtitle-3">
                {{ tableTitle }}
              </h2>
              <div class="mb-6 overflow-y-auto md:max-w-full" style="max-width: 80rem">
                <vf-table :aria-describedby="getHeadingId(tabs[i].title, tableTitle)" class="ws-pre-wrap text-center text-sm">
                  <template #head>
                    <tr>
                      <th
                        v-for="({ label }, key) in getTableData(list, `${categoryTitle}${i}${k}`, 'header', order)"
                        :key
                        class="b px-2 py-5"
                        :class="[brandClasses.table.header, { 'sticky left-0': !key }]"
                        scope="col"
                        :style="{ 'min-width': key ? '6.75rem' : '7.75rem' }"
                      >
                        {{ label }}
                      </th>
                    </tr>
                  </template>
                  <tr
                    v-for="(item, key) in getTableData(list, `${categoryTitle}${i}${k}`, 'rows', order)"
                    :key
                    :class="{ 'bg-grey-90': key % 2 }"
                  >
                    <template
                      v-for="({ size }, l) in getTableData(list, `${categoryTitle}${i}${k}`, 'header', order)"
                      :key="l"
                    >
                      <td class="b px-2 py-5" :class="brandClasses.table.rows">
                        {{ item[size] }}
                      </td>
                    </template>
                  </tr>
                </vf-table>
              </div>
              <div v-if="sizeChartFits?.data">
                <div v-if="staticPageTabs?.[i]?.accordions[j]?.illustrationTitle" class="mb-8 title-4">
                  {{ staticPageTabs[i].accordions[j].illustrationTitle }}
                </div>
                <h3 class="my-4 subtitle-3">
                  {{ $t.fits }}
                </h3>
                <div class="flex wrap">
                  <template v-for="({ description, title: fitsTitle, image, video }, p) in sizeChartFits.data" :key="p">
                    <div v-style:max-w="{ sm: '50%', lg: '14.5rem' }" class="mb-4 px-2">
                      <vf-video
                        v-if="video"
                        v-bind="video"
                      />
                      <base-picture
                        v-else-if="image"
                        v-style:w="{ sm: '100%', lg: '12.5rem' }"
                        v-bind="image"
                        class="shrink-0"
                      />
                      <h2 class="my-4">
                        {{ fitsTitle }}
                      </h2>
                      <p class="fw-normal">
                        {{ description }}
                      </p>
                    </div>
                  </template>
                </div>
              </div>
              <div
                v-if="chartMeasurements?.data"
                class="my-8 f-col items-start gap-6 b-t b-grey-60 md:flex-row md:gap-10"
                :class="measurements.brandClasses.container"
                :data-scroll-el="`howToMeasure-${categoryTitle}-${i}-${measurementsIndex}`"
              >
                <div class="mt-4 text-sm space-y-6" :class="brandClasses?.measurementSection">
                  <div>
                    <base-picture
                      v-if="chartMeasurements.data?.image"
                      class="shrink-0 <md:w-full"
                      data-test-id="measurement-chart-image"
                      height="390"
                      :src="chartMeasurements.data?.image"
                      width="288"
                    />
                    <vf-carousel v-if="chartMeasurements.data?.pictures" class="w-full shrink-0 md:w-70">
                      <base-picture
                        v-for="(picture, key) in chartMeasurements.data.pictures"
                        :key
                        v-bind="picture"
                        class="w-full"
                        :height="{ sm: 662, md: 420 }"
                        :width="{ sm: 652, md: 280 }"
                      />
                    </vf-carousel>
                  </div>
                  <div>
                    <div class="mb-4 space-y-2">
                      <h2 v-if="chartMeasurements.data?.title" :class="measurements.brandClasses.type">
                        {{ chartMeasurements.data.title }}
                      </h2>
                      <h2 v-if="staticPageTabs?.[i]?.measurements?.title" :class="measurements.brandClasses.type">
                        {{ staticPageTabs[i].measurements.title }}
                      </h2>
                      <p v-if="chartMeasurements.data?.subtitle">
                        {{ chartMeasurements.data.subtitle }}
                      </p>
                    </div>
                    <ol :class="measurements.brandClasses.contentContainer || 'space-y-4'">
                      <li
                        v-for="{ no, title: contentTitle, description, images } in chartMeasurements.data?.texts"
                        :key="no"
                        class="flex gap-2"
                        :class="measurements.brandClasses.contentItem"
                        style="align-content: flex-start"
                      >
                        <div
                          v-if="measurements.number"
                          class="h-6 w-6 flex shrink-0 center rounded-full text-sm c-white"
                          :class="measurements.brandClasses.number"
                        >
                          {{ no }}
                        </div>
                        <div class="space-y-1">
                          <p
                            v-if="contentTitle"
                            :class="measurements.brandClasses.title"
                            :data-test-id="`measurement-chart-details-title-${no}`"
                          >
                            {{ contentTitle }}:
                          </p>
                          <p class="fw-normal" :class="measurements.brandClasses.description">
                            {{ description }}
                          </p>
                          <template v-if="images && measurements.footer">
                            <div v-for="({ src, imgTitle }, key) in images" :key class="w-fit">
                              <base-picture :src />
                              <p class="text-center uppercase" :class="measurements.brandClasses.imgTitle">
                                {{ imgTitle }}
                              </p>
                            </div>
                          </template>
                        </div>
                      </li>
                    </ol>
                  </div>
                  <template v-if="chartMeasurements.data?.footer && measurements.footer">
                    <div v-for="{ no, title: footerTitle, description } in chartMeasurements.data.footer" :key="no" class="mt-1">
                      <p class="subtitle-4">
                        {{ footerTitle }}
                      </p>
                      <p class="body-3 fw-normal">
                        <base-interpolator :content="description">
                          <template #link="{ args: [message, to] }">
                            <vf-link :to>
                              {{ message }}
                            </vf-link>
                          </template>
                        </base-interpolator>
                      </p>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </vf-accordion>
          <!-- Kids Sizing -->
          <section v-if="kids && i === 2">
            <h3 class="mt-5 title-3">
              {{ kids.description }}
            </h3>
            <p class="mb-6 mt-2 text-sm">
              {{ kids.title }}
            </p>
            <vf-button size="xs" :to="kids.ctaUrl">
              {{ kids.ctaLabel }}
              <vf-icon class="ml-4" name="download" size="md" />
            </vf-button>
          </section>
        </template>
      </vf-tabs>
    </section>
    <!-- WRONG SIZE -->
    <section v-if="measureHow" class="text-center space-y-4">
      <h3 class="title-3">
        {{ measureHow.wrongSize }}
      </h3>
      <p>
        {{ measureHow.freeReturns }}
        <span v-html="measureHow.findOutMore" class="mt-2" />
      </p>
    </section>
    <!-- FAQ -->
    <section v-if="faq" class="pt-12 container">
      <h2 class="mb-8 text-center subtitle-3">
        {{ faq.heading }}
      </h2>
      <div class="mb-10 f-col">
        <template v-for="({ question, answer }, key) in faq.questions" :key>
          <h3 class="mb-2 subtitle-3">
            {{ question }}
          </h3>
          <p v-html="answer" class="mb-8 ws-pre-wrap body-3" />
        </template>
        <div class="flex justify-center b-b b-grey-60 pb-8">
          <div v-for="(src, key) in faq.images" :key class="w-1/2 md:w-a md:px-8 <md:px-4">
            <base-picture v-bind="src" height="160" width="160" />
          </div>
        </div>
      </div>
    </section>
    <!-- Footer -->
    <section v-if="footer" class="my-16 b p-4 md:text-center">
      <h3 class="mb-2 title-3">
        {{ footer.title }}
      </h3>
      <p v-if="footer.description" class="ws-pre-line">
        <base-interpolator :content="footer.description">
          <template #link="{ args: [message, to] }">
            <vf-link :to>
              {{ message }}
            </vf-link>
          </template>
        </base-interpolator>
      </p>
    </section>
  </main>
</template>

<script lang="ts" setup>
import type { ChartData, ChartTabData, SizeChartData } from '#core/server/types/sizeChart'

const emit = defineEmits<{
  'change-page-title': [title: string]
}>()

const { titleBelowBreadcrumb, sizeChart } = useAppConfig().pages.help
const { measurements } = useAppConfig().components.dialog.sizeChart
const { brandClasses, brandStyles, defaultToggle, sizeChartDataId, staticPageId } = sizeChart
const { $feature } = useNuxtApp()
const locale = useLocale()
const countryCode = useCountryCode().toLowerCase()
const route = useRoute()
const categoryQuery = useRouteQuery('category')
const productQuery = useRouteQuery('product')
const activeAccordion = ref('0')
const activeTab = ref('0')
const toggle = reactive({})
const host = useHost()

useHead({
  link: () => [{ rel: 'canonical', href: `https://${host}${route.path}` }]
})

const category = computed({
  get: () => categoryQuery.value,
  set: (v: string | null) => categoryQuery.value = isString(v) ? v.toLowerCase() : v
})

const product = computed({
  get: () => productQuery.value,
  set: (v: string | null) => productQuery.value = isString(v) ? v.toLowerCase() : v
})

const apiLocale = route.path.split('/').some((segment) => segment.endsWith('-es'))
  ? `es-es` as ReturnType<typeof useLocale>
  : locale

const api = useApi(route, apiLocale)

const { data, error } = await api.internal.getStaticPage(staticPageId)
if (!data.value) throw createError({ ...error.value!, fatal: true })
const { title, pageDescription, faq, howToMeasureLink, footer, kids, measureHow, tabs: staticPageTabs } = data.value

const { data: chartData } = $feature.enableApiSizeCharts
  ? await api.products.sizeChartById<SizeChartData>({ sizeChartId: sizeChartDataId }, {
      key: `products-size-chart-${sizeChartDataId}`
    })
  : await api.internal.getSizeChart<SizeChartData>(sizeChartDataId)

const { tabs } = chartData.value!
for (const [i, items] of tabs.entries()) {
  for (const categories of Object.values(items.pageData || {})) {
    categories.forEach(({ list, category }, j) => {
      toggle[`${category}${i}${j}`] = list.find(({ toggle }) => defaultToggle[countryCode]?.slice(0, 2).includes(toggle.toLowerCase()))?.toggle || ''
    })
  }
}

emit('change-page-title', title)

activeTab.value = `${Math.max(tabs.findIndex((tab) => tab.title.toLowerCase() === category.value), 0)}`

activeAccordion.value = `${Object.keys(tabs[Math.max(+activeTab.value, 0)].pageData ?? {})?.findIndex((label) =>
  product.value === label.toLowerCase())}`

const updateQuery = (active: string, label?: string) => {
  const queryParams = {
    ...route.query,
    ...(tabs[active]?.title
      ? { category: tabs[active].title.toLowerCase() }
      : {}),
    ...(label ? { product: label.toLowerCase() } : {})
  }
  const searchParams = new URLSearchParams(queryParams).toString()
  const url = `${route.path}?${searchParams}${route.hash}`
  history.replaceState(null, '', url)
}

const onTabChange = (active: string) => {
  updateQuery(active)
}

const getHeadingId = (category?: string, title?: string) =>
  `heading-${category ?? ''}-${(title ?? '').replace(/\s+/g, '')}`.toLowerCase()

const getTableData = (
  list: ChartData[],
  category: string,
  content: 'header' | 'rows',
  order?: string[] | Record<typeof locale | 'default', string[]>
) => {
  const items = (list.find((item) => item.toggle === toggle[category]) ?? list[0])?.table
  if (!items) return []
  const headers = Object.keys(items[0]).map((size) => ({ size, label: size }))

  if (!order) return content === 'header' ? headers : items
  const ordering = isArray(order) ? order : (order[locale] || order.default)
  const orderMap = Object.fromEntries(ordering.map((size, index) => [size, index]))

  return content === 'header' ? headers.sort((a, b) => orderMap[a.size] - orderMap[b.size]) : items
}

const normalizeMeasurementsData = (items: ChartTabData[]) => {
  if (items?.length === 0) return items

  const result = [...items]
  let groupStart = 0

  for (let i = 0; i < items.length; i++) {
    const current = items[i]
    const next = items[i + 1]

    const isEndOfGroup
      = !next?.measurements?.id
        || current?.measurements?.id !== next.measurements.id

    if (
      current?.measurements?.id
      && next?.measurements?.id
      && current.measurements.id === next.measurements.id
    ) {
      result[i] = {
        ...current,
        measurements: {
          contentType: 'measurements',
          title: '',
          dataId: ''
        }
      }
    }

    if (isEndOfGroup) {
      for (let j = groupStart; j <= i; j++) {
        result[j] = {
          ...result[j],
          measurementsIndex: i
        }
      }
      groupStart = i + 1
    }
  }

  return result
}
</script>
